import { hasCSSSelectorSupport } from "@otto-ec/otto-components-utils/utils/canIUse";
import { mount, unmount, type Component } from "svelte";
import { extendElementWithActionAttributes } from "../customElement/extendElementWithActionAttributes.ts";
import { useShadowStylesInjector } from "./utils/useShadowStylesInjector.ts";
import { setHost } from "./utils/useHost.ts";
import { createObservedAttributes } from "./utils/createObservedAttributes.ts";
import type { DefineComponentOptions, DefineComponentProps } from "./types.ts";
import { setMounted } from "./utils/setMounted.ts";
import { resolveComponent } from "./utils/resolveComponent.ts";
import { parseAttributeToValue, stringifyValueToAttribute } from "./utils/parseAttributeToValue.ts";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

export function defineComponent<Props extends Record<string, unknown>>({
  tag,
  styles,
  props,
  html,
  extendOptions = {},
  onMount,
  component,
}: DefineComponentOptions<Props>): void {
  const injectShadowStyles = useShadowStylesInjector(styles);

  const propsDefinitions = props as DefineComponentProps<Record<string, unknown>>;
  const attributeMetaMap = Object.fromEntries(
    Object.entries(propsDefinitions).map(
      ([propName, { type, attribute = propName }]) =>
        [attribute as string, { propName: propName as string, type }] as const,
    ),
  );

  /**
 *
 *
 */
  /*                                                       */
  const reflecting = new Set<string>();

  let ElementClass = class extends HTMLElement implements Record<string, unknown> {
    static observedAttributes = createObservedAttributes(propsDefinitions);

    mounted: boolean = false;
    #ocEffectRootUnset?: () => void;
    $ocProps: Record<string, unknown> = {};
    $ocSvelteComponent?: Record<string, unknown>;
    $onDestroyCallbacks: ((() => void) | void | undefined)[] = [];
    [x: string]: unknown;

    attributeChangedCallback(name: string, _: string | null, newValue: string | null): void {
      if (reflecting.has(name)) return;
      const { propName, type } = attributeMetaMap[name];
      const value = parseAttributeToValue(newValue, type);

      if (this.$ocProps[propName] === value) return;

      this.$ocProps[propName] = value;
      if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;
    }

    async connectedCallback(): Promise<void> {
      if (html) (this.shadowRoot ? this.shadowRoot : this).innerHTML = html;

      /*                                                                              */
      const resolvedComponentOrPromise = component ? resolveComponent(component) : undefined;
      const resolvedComponent =
        Promise.resolve(resolvedComponentOrPromise) === resolvedComponentOrPromise
          ? await resolvedComponentOrPromise
          : (resolvedComponentOrPromise as Component | undefined);

      /*                                                        */
      Object.entries(propsDefinitions).forEach(([propName, { type }]) => {
        if (Object.hasOwn(this.$ocProps, propName)) return;
        if (Object.hasOwn(this, propName)) this.$ocProps[propName] = this[propName];
        else this.$ocProps[propName] = type === "Boolean" ? false : undefined;
      });

      if (resolvedComponent) {
        this.#ocEffectRootUnset = $effect.root(() => {
          /*                                                                          */
          setHost(this);

          /*                                                                                   */
          this.$ocSvelteComponent = mount(resolvedComponent, {
            target: this.shadowRoot ? this.shadowRoot : this,
            props: this.$ocProps,
          });

          /*                                          */
          Object.assign(this, this.$ocSvelteComponent, this.$ocProps);

          /*                                                          */
          /*                                                   */
          setHost(undefined);

          /*                                                              */
          /*                                                              */
          Object.keys(propsDefinitions).forEach((propName) => {
            /*                                                                                                */
            /*                                                                                                                 */
            let isFirstRun = true;
            $effect(() => {
              /*                                                                                        */
              const [value] = [this.$ocSvelteComponent![propName]] as const;

              /*                                                                      */
              /*                                                                       */
              /*                                                                                                                   */
              if (!isFirstRun || value !== undefined)
                (this as Record<string, unknown>)[propName] = value;

              isFirstRun = false;
            });
          });
        });
      }

      await injectShadowStyles(this);

      /*                                                                         */
      this.$onDestroyCallbacks.push(onMount?.(this as never, this.$ocProps as never));

      setMounted(tag, this, true);
    }

    async disconnectedCallback(): Promise<void> {
      this.#ocEffectRootUnset?.();
      if (this.$ocSvelteComponent) {
        await unmount(this.$ocSvelteComponent);
        this.$ocSvelteComponent = undefined;
      }

      /*                                                         */
      this.$onDestroyCallbacks.forEach((callback) => callback?.());
      this.$onDestroyCallbacks.length = 0;

      setMounted(tag, this, false);
    }
  };

  Object.entries(propsDefinitions).forEach(
    ([propName, { type, attribute = propName, reflect }]) => {
      Object.defineProperty(ElementClass.prototype, propName, {
        get(this: InstanceType<typeof ElementClass>): unknown {
          return this.$ocProps[propName];
        },
        set(this: InstanceType<typeof ElementClass>, value: unknown) {
          if (this.$ocProps[propName] === value) return;
          this.$ocProps[propName] = value;

          if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;

          /*                                                                   */
          /*                                                                   */
          /*                                                                  */
          /*                                                                   */
          /*                                                                    */
          if (reflect) {
            reflecting.add(propName);
            stringifyValueToAttribute(this, attribute, value, type);
            reflecting.delete(propName);
          }
        },
      });
    },
  );

  ElementClass = window.__components.extend(extendOptions)(ElementClass) as never;

  /*                                             */
  /*                                                    */
  if (!hasCSSSelectorSupport)
    ElementClass = extendElementWithActionAttributes(ElementClass as never) as never;

  customElements.define(tag, ElementClass);
}

window.__components ??= {} as never;
window.__components.defineComponent = defineComponent;
